import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import V2Shell, { ShellSwitcher } from './components/V2Shell';
import useProduction from '@/src/Production/components/hooks/useProduction';
import {
    IconPipeline,
    IconStaff,
    IconGear,
    IconDesign,
    IconVenues,
    IconContacts,
    IconSettings,
    IconPlus,
} from './components/icons';

/*
 * Jobs-section chrome: entering Jobs swaps the dock to the section's
 * surfaces (jobs list, staff, gear, design, venues, contacts, settings),
 * same pattern as the event chrome. Pages carry their location in the
 * eyebrow ("Jobs · Staff").
 */

const EVENT_TYPES = [
    'brand_activation',
    'conference',
    'party',
    'wedding',
    'gala',
    'holiday_party',
    'tour',
    'photoshoot',
    'pop_up',
    'launch',
    'headshots',
    'other',
];

export default function V2JobsChrome() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { createJob, getJobs } = useProduction();
    const base = '/v2/jobs';

    /* on a job page the context switcher becomes the breadcrumb picker:
       Jobs / <job> ▾ — searchable, with + Job / All jobs in the footer */
    const jobMatch = pathname.match(/^\/v2\/jobs\/(\d+)/);
    const jobId = jobMatch ? +jobMatch[1] : null;
    const [jobRows, setJobRows] = useState<any[]>([]);

    useEffect(() => {
        if (jobId == null) return;
        let mounted = true;
        getJobs()
            .then((rows) => {
                if (mounted) setJobRows(rows ?? []);
            })
            .catch(() => null);
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId != null]);

    const [createOpen, setCreateOpen] = useState(false);
    const [fields, setFields] = useState({
        name: '',
        date: moment().add(1, 'month').format('YYYY-MM-DD'),
        eventType: 'brand_activation',
        status: 'pending',
    });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(false);

    const submitCreate = async () => {
        if (!fields.name.trim() || creating) return;
        setCreating(true);
        setCreateError(false);
        const created = await createJob({
            name: fields.name.trim(),
            status: fields.status,
            eventType: fields.eventType,
            startAt: moment(fields.date).hour(18).toISOString(),
        }).catch(() => null);
        setCreating(false);
        if (created?.id) {
            setCreateOpen(false);
            setFields((f) => ({ ...f, name: '' }));
            navigate(`/v2/jobs/${created.id}`);
        } else {
            setCreateError(true);
        }
    };

    const sectionSwitcher: ShellSwitcher = {
        label: 'Jobs',
        items: [
            {
                key: 'home',
                label: 'Home',
                onClick: () => navigate('/v2'),
            },
            {
                key: 'activations',
                label: 'Activations',
                onClick: () => navigate('/v2/activations'),
            },
            {
                key: 'jobs',
                label: 'Jobs',
                active: true,
                onClick: () => navigate('/v2/jobs'),
            },
        ],
    };

    const jobs = jobRows.filter((row: any) => row.jobId != null);
    const currentJob = jobs.find((row: any) => +row.jobId === jobId);
    const jobSwitcher: ShellSwitcher = {
        prefix: 'Jobs /',
        label: currentJob?.name ?? `#${jobId}`,
        header: 'Switch job',
        searchable: true,
        items: jobs.map((row: any) => ({
            key: String(row.jobId),
            label: row.name,
            sublabel: row.startAt
                ? moment(row.startAt).format('MMM D')
                : undefined,
            active: +row.jobId === jobId,
            onClick: () => navigate(`/v2/jobs/${row.jobId}`),
        })),
        footer: [
            {
                key: 'new',
                label: '+ Job',
                onClick: () => setCreateOpen(true),
            },
            {
                key: 'all',
                label: 'All jobs',
                onClick: () => navigate('/v2/jobs'),
            },
        ],
    };

    return (
        <V2Shell
            switcher={jobId != null ? jobSwitcher : sectionSwitcher}
            items={[
                {
                    key: 'jobs',
                    title: 'Jobs',
                    icon: <IconPipeline />,
                    active: pathname === base,
                    onClick: () => navigate(base),
                },
                {
                    key: 'staff',
                    title: 'Staff',
                    icon: <IconStaff />,
                    active: pathname.startsWith(`${base}/staff`),
                    onClick: () => navigate(`${base}/staff`),
                },
                {
                    key: 'gear',
                    title: 'Gear',
                    icon: <IconGear />,
                    active: pathname.startsWith(`${base}/gear`),
                    onClick: () => navigate(`${base}/gear`),
                },
                {
                    key: 'design',
                    title: 'Design',
                    icon: <IconDesign />,
                    active: pathname.startsWith(`${base}/design`),
                    onClick: () => navigate(`${base}/design`),
                },
                {
                    key: 'venues',
                    title: 'Venues',
                    icon: <IconVenues />,
                    active: pathname.startsWith(`${base}/venues`),
                    onClick: () => navigate(`${base}/venues`),
                },
                {
                    key: 'contacts',
                    title: 'Contacts',
                    icon: <IconContacts />,
                    active: pathname.startsWith(`${base}/contacts`),
                    onClick: () => navigate(`${base}/contacts`),
                },
                {
                    key: 'settings',
                    title: 'Settings',
                    icon: <IconSettings />,
                    active: pathname.startsWith(`${base}/settings`),
                    onClick: () => navigate(`${base}/settings`),
                },
            ]}
            actions={[
                {
                    key: 'new',
                    title: 'New job',
                    icon: <IconPlus />,
                    onClick: () => setCreateOpen(true),
                },
            ]}
        >
            {createOpen && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setCreateOpen(false)}
                >
                    <div
                        className="w-full max-w-[400px] rounded-[15px] border border-border bg-background p-5 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') setCreateOpen(false);
                        }}
                    >
                        <div className="ps-label mb-3.5">New job</div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                            Name
                        </label>
                        <input
                            autoFocus
                            className="ps-input mb-3.5 w-full text-[13.5px]"
                            placeholder="Client — Event"
                            value={fields.name}
                            onChange={(e) =>
                                setFields((f) => ({
                                    ...f,
                                    name: e.target.value,
                                }))
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submitCreate();
                            }}
                        />
                        <div className="mb-4 flex flex-wrap gap-4">
                            <div>
                                <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                    Event date
                                </label>
                                <input
                                    type="date"
                                    className="ps-input text-[13px]"
                                    value={fields.date}
                                    onChange={(e) =>
                                        setFields((f) => ({
                                            ...f,
                                            date: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                    Type
                                </label>
                                <select
                                    className="ps-input cursor-pointer appearance-none text-[13px]"
                                    value={fields.eventType}
                                    onChange={(e) =>
                                        setFields((f) => ({
                                            ...f,
                                            eventType: e.target.value,
                                        }))
                                    }
                                >
                                    {EVENT_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                            {t.replaceAll('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                    Status
                                </label>
                                <select
                                    className="ps-input cursor-pointer appearance-none text-[13px]"
                                    value={fields.status}
                                    onChange={(e) =>
                                        setFields((f) => ({
                                            ...f,
                                            status: e.target.value,
                                        }))
                                    }
                                >
                                    <option value="pending">
                                        Pending (pipeline)
                                    </option>
                                    <option value="ready_for_production">
                                        Ready for production
                                    </option>
                                    <option value="in_production">
                                        In production
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={creating || !fields.name.trim()}
                                onClick={submitCreate}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-white disabled:opacity-50"
                            >
                                {creating ? 'Creating…' : 'Create job'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setCreateOpen(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                            {createError && (
                                <span className="text-[12px] font-semibold text-attention">
                                    Create failed
                                </span>
                            )}
                        </div>
                        <p className="mt-3 text-[11px] text-faint">
                            The job starts with one activation. Pending jobs
                            stay off the Activations list until they go live;
                            add more activations from the job page.
                        </p>
                    </div>
                </div>
            )}
        </V2Shell>
    );
}
